import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Switch, FormControlLabel, TextField, Button, Stack, Grid, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { getPaymentGateways, updatePaymentGateway } from '../../api/endpoints/settings';

const DEFAULT_GATEWAYS = [
  { code: 'razorpay', name: 'Razorpay', enabled: true, key: '', secret: '' },
  { code: 'stripe', name: 'Stripe', enabled: false, key: '', secret: '' },
  { code: 'paypal', name: 'PayPal', enabled: false, key: '', secret: '' },
  { code: 'paytm', name: 'Paytm', enabled: false, key: '', secret: '' },
];

const PaymentSettings = () => {
  const [gateways, setGateways] = useState(DEFAULT_GATEWAYS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      setLoading(true);
      const res = await getPaymentGateways();
      const data = res.data.data || [];
      if (Array.isArray(data) && data.length) {
        setGateways(data);
      }
    } catch {
      // use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (code) => {
    setGateways((prev) => prev.map((g) => g.code === code ? { ...g, enabled: !g.enabled } : g));
  };

  const handleChange = (code, field, value) => {
    setGateways((prev) => prev.map((g) => g.code === code ? { ...g, [field]: value } : g));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      for (const g of gateways) {
        await updatePaymentGateway(g.code, g);
      }
      toast.success('Payment settings saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <PageHeader title="Payment Settings" subtitle="Configure payment gateways" />
      <Stack spacing={3}>
        {gateways.map((g) => (
          <Card key={g.code}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>{g.name}</Typography>
                <FormControlLabel control={<Switch checked={g.enabled} onChange={() => handleToggle(g.code)} />} label="Enabled" />
              </Stack>
              {g.enabled && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="API Key" size="small" value={g.key} onChange={(e) => handleChange(g.code, 'key', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="API Secret" size="small" type="password" value={g.secret} onChange={(e) => handleChange(g.code, 'secret', e.target.value)} />
                    </Grid>
                  </Grid>
                </Stack>
              )}
            </CardContent>
          </Card>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Payment Settings'}</Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default PaymentSettings;
